import React, { useState } from "react";
import { Lock, Mail, User, ArrowRight, Loader2 } from "lucide-react";
import { useAdminAuth } from "../context/AdminAuthContext";
import { motion, AnimatePresence } from "framer-motion";

const AdminLogin = () => {
  const { login } = useAdminAuth();
  const [isRegister, setIsRegister] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", password: "" });
  const [status, setStatus] = useState({ loading: false, error: "", success: "" });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus({ loading: true, error: "", success: "" });

    const endpoint = isRegister ? "/register" : "/login";
    const url = `http://localhost:2027/api/admin/auth${endpoint}`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const text = await response.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { message: text }; }

      if (response.ok) {
        if (isRegister) {
          setStatus({ 
            loading: false, 
            error: "", 
            success: "Registration Successful! Please wait for Super Admin approval." 
          });
          setFormData({ name: "", email: "", password: "" });
        } else {
          login(data.token, data.role, data.name, data.permissions || "{}");
        }
      } else {
        setStatus({ loading: false, error: data.message || "Authentication Failed", success: "" });
      }
    } catch (err) {
      setStatus({ loading: false, error: "Server Error. Check Backend Connection.", success: "" });
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4 font-sans">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-[450px] bg-white p-10 rounded-[32px] shadow-[0_20px_50px_rgba(0,0,0,0.05)] border border-slate-100"
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={isRegister ? "register" : "login"}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-[#0F172A] mb-2 tracking-tight">
                {isRegister ? "Join Merinal Access" : "Admin Login"}
              </h2>
              <p className="text-slate-500 text-sm">
                Please enter your credentials to proceed.
              </p>
            </div>

            {status.success && (
              <div className="mb-6 p-4 bg-emerald-50 border border-emerald-100 rounded-2xl">
                <p className="text-emerald-700 text-sm font-medium text-center">{status.success}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              {isRegister && (
                <div className="space-y-1">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 ml-1">Full Name</label>
                  <div className="relative group">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#E67E22] transition-colors" size={18} />
                    <input 
                      name="name"
                      type="text" 
                      placeholder="Frog Officer Name"
                      className="w-full bg-slate-50 border border-slate-200 py-4 pl-12 pr-4 rounded-2xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#E67E22]/20 focus:border-[#E67E22] transition-all placeholder:text-slate-400 shadow-sm"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
              )}

              <div className="space-y-1">
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 ml-1">Corporate Email</label>
                <div className="relative group">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#E67E22] transition-colors" size={18} />
                  <input 
                    name="email"
                    type="email" 
                    placeholder="admin@tradyfrog.com"
                    className="w-full bg-slate-50 border border-slate-200 py-4 pl-12 pr-4 rounded-2xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#E67E22]/20 focus:border-[#E67E22] transition-all placeholder:text-slate-400 shadow-sm"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 ml-1">Security Key</label>
                <div className="relative group">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#E67E22] transition-colors" size={18} />
                  <input 
                    name="password"
                    type="password" 
                    placeholder="••••••••"
                    className="w-full bg-slate-50 border border-slate-200 py-4 pl-12 pr-4 rounded-2xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#E67E22]/20 focus:border-[#E67E22] transition-all placeholder:text-slate-400 shadow-sm"
                    value={formData.password}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              {status.error && <p className="text-red-500 text-xs font-medium text-center bg-red-50 py-2 rounded-lg">{status.error}</p>}

              <button 
                disabled={status.loading}
                className="w-full bg-[#E67E22] hover:bg-[#D35400] text-white font-bold py-4 rounded-2xl shadow-[0_10px_20px_rgba(230,126,34,0.3)] transition-all flex items-center justify-center gap-2 group disabled:opacity-70 mt-4 uppercase tracking-wider text-sm"
              >
                {status.loading ? <Loader2 className="animate-spin" /> : (isRegister ? "Request Access" : "Authorize Access")} 
                {!status.loading && <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />}
              </button>
            </form>
          </motion.div>
        </AnimatePresence>

        <div className="mt-10 text-center border-t border-slate-100 pt-6">
          <p className="text-slate-400 text-[11px] uppercase tracking-widest font-bold mb-2">
            {isRegister ? "Already have access?" : "Don't have an account?"}
          </p>
          <button 
            onClick={() => {
              setIsRegister(!isRegister);
              setStatus({ loading: false, error: "", success: "" });
            }}
            className="text-[#E67E22] font-extrabold uppercase text-[11px] tracking-[0.2em] hover:text-[#D35400] transition-colors"
          >
            {isRegister ? "Authorize Here" : "Create New Request"}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLogin;